import React, {useCallback, useEffect} from 'react';
import {connect} from 'react-redux';
import {useSelector, useDispatch} from 'react-redux';
import './Funtone.scss';
import {
  ScreensEnum,
  PopUpEnum,
  doSearch,
  getCatalog,
  setPopUp,
  closePopUp,
  setSelectedSubscriber,
  getSubscribersData,
  hideToast,
  showToast,
  deleteSong,
  deleteContact,
  getGallery,
  setSelectedSong,
  resetState,
  handleBrowserBack,
  setCurrentScreen,
} from './FuntoneSlice';
import Catalog from './Screens/Catalog/Catalog';
import SearchPage from './Screens/SearchPage/SearchPage';
import SongsPage from './Screens/SongsPage/SongsPage';
import ErrorPage from './Screens/ErrorPage/ErrorPage';
import FuntoneScreen from './Components/FuntoneScreen/FuntoneScreen';
import PopUp from '../../Shared/Popup/PopUp';
import LoginPopUp from './PopUps/LoginPopUp/LoginPopUp';
import ConfirmationPopUp from './PopUps/ConfirmationPopUp/ConfirmationPopUp';
import ErrorStep from 'app/Components/Shared/ErrorStep/ErrorStep';
import {ErrorContent} from './Utils/ErrorContent';
import SelectSubscriber from 'app/Components/Shared/SelectSubscriber/SelectSubscriber';
import {useScreenNavigation} from './Utils/ScreenNavigationUtils';
import {ErrorsEnum} from './Enums/ErrorsEnum';
import PackageSelect from './Screens/PackageSelect/PackageSelect';
import SuccessPage from './Screens/SuccessPage/SuccessPage';
import AdminPanel from './Screens/AdminPanel/AdminPanel';
import {
  dwhFuntoneSongDelete,
  dwhFuntoneSongDeleteSuccess,
  dwhFuntoneSongDeleteFailed,
  dwhFuntoneRemoveSongFromPlaylist,
  dwhFuntoneRemoveSongFromPlaylistSuccess,
  dwhFuntoneRemoveSongFromPlaylistFailed,
  dwhFuntoneRemoveSongFromContact,
  dwhFuntoneRemoveSongFromContactSuccess,
  dwhFuntoneRemoveSongFromContactFailed,
} from './Reports/dwhReports';
import {getCommonReportParams} from './Reports/reportHelpers';
import SongSettings from './Screens/SongSettings/SongSettings';
import ConfigureSongs from './Screens/ConfigureSongs/ConfigureSongs';
import TermsAndConditions from './Screens/TermsAndConditions/TermsAndConditions';
import Toast from './Components/Toast/Toast';

// Helper function to validate subscriber and return appropriate error
const validateSubscriberForError = (subscriber) => {
  const validationResults = subscriber?.validationResults;
  if (!validationResults) return null;

  // Check for business account
  if (validationResults.IsBusinessAccount) {
    return ErrorsEnum.IS_BUSINESS;
  }

  // Check for lost or stolen suspension
  if (validationResults.SubStatusLostOrStolen) {
    return ErrorsEnum.SUSPENDED_SUBSCRIBER_LOST_OR_STOLEN;
  }

  // Check for suspended status
  if (validationResults.SubStatus) {
    return ErrorsEnum.SUSPENDED;
  }

  // Check for data only
  if (validationResults.SwitchCode) {
    return ErrorsEnum.DATA_ONLY;
  }

  // Check for ניוד (in transfer)
  if (validationResults.NPStatus) {
    return ErrorsEnum.IN_TRANSFER;
  }

  // Check for both debt and payment method issues
  if (validationResults.Debt && validationResults.PaymentMethod) {
    return ErrorsEnum.FINANCIAL_PAYMENT_UPDATE_ERROR;
  }

  // Check for debt only
  if (validationResults.Debt) {
    return ErrorsEnum.FINANCIAL_DEBT_ERROR;
  }

  // Check for payment method only
  if (validationResults.PaymentMethod) {
    return ErrorsEnum.FINANCIAL_PAYMENT_METHOD_UPDATE_ERROR;
  }

  return null;
};

const Funtone = (props) => {
  const dispatch = useDispatch();
  const {funtoneSlice = {}, popUp} = props.funtoneSlice
    ? {funtoneSlice: props.funtoneSlice, popUp: props.funtoneSlice.popUp}
    : {funtoneSlice: {}, popUp: null};
  const {currentScreen, toast, isGalleryMode, isEditPlaylistFlow, isChangeDefaultSongFlow} = props.funtoneSlice || {};
  const {setPopUp, closePopUp, getSubscribersData, setSelectedSubscriber} = props;
  const loginData = props.loginData;
  const {isOtpAuthenticated, isFullAuthenticated, isOtpExtendedAuthenticated, isSilentOtpAuthenticated} = loginData;
  const isAuthenticated = isFullAuthenticated || isOtpAuthenticated || isOtpExtendedAuthenticated || isSilentOtpAuthenticated;

  // Read via useSelector so React's equality check uses the actual value rather
  // than the whole slice object reference. This means the effect below only
  // re-runs when subscribersData/isSubscribersFetching genuinely change
  // (e.g. null→array on load, or array→null when state is restored from history)
  // and never on unrelated dispatches that touch other slice fields.
  const subscribersData = useSelector((state) => state.FuntoneSlice.subscribersData);
  const isSubscribersFetching = useSelector((state) => state.FuntoneSlice.isSubscribersFetching);
  const isSubscribersFetchFailed = useSelector((state) => state.FuntoneSlice.isSubscribersFetchFailed);

  useEffect(() => {
    if (isAuthenticated && !subscribersData && !isSubscribersFetching && !isSubscribersFetchFailed) getSubscribersData();
  // isAuthenticated (boolean) instead of loginData (object) prevents the effect
  // from firing on every Redux dispatch that creates a new loginData reference.
  }, [isAuthenticated, subscribersData, isSubscribersFetching, isSubscribersFetchFailed, getSubscribersData]);

  const {navigateToError, navigateToPackageSelect, navigateToCatalog} = useScreenNavigation();

  // Ensure mobile browser UI uses dark theme (status bar, address bar backgrounds)
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Helper to upsert meta tag
    function upsertMeta(name, content, attr = 'name') {
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    }

    // Set theme color for Chrome, Edge, Android to match dark theme background
    upsertMeta('theme-color', '#13051a');

    // Set iOS status bar appearance (defaults: default/black/black-translucent)
    // Note: Apple prefers <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent">
    upsertMeta('apple-mobile-web-app-status-bar-style', 'black-translucent');

    // Indicate dark color-scheme preference
    upsertMeta('color-scheme', 'dark', 'name');

    // Add a dark class to root element so CSS dark variables apply
    document.documentElement.classList.add('theme-dark');

    return () => {
      // we don't remove these tags on unmount — keep UI consistent across app
    };
  }, []);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  // Browser history management - handle popstate events (browser back/forward)
  useEffect(() => {
    const handlePopState = (event) => {
      // Prevent default browser back behavior
      event.preventDefault();
      
      // Get the state from browser history
      const historyState = event.state;

      // If navigating to SONGS_PAGE with a genre ID, restauration is handled inside handleBrowserBack reducer
      // Dispatch the handleBrowserBack action with the history state
      dispatch(handleBrowserBack(historyState));
    };

    // Add the event listener
    window.addEventListener('popstate', handlePopState);

    // Initialize browser history with current screen if not already set
    if (typeof window !== 'undefined' && window.history && currentScreen) {
      try {
        const stateData = {
          screen: currentScreen,
          timestamp: Date.now(),
          isInitial: true
        };
        window.history.replaceState(stateData, '', `#${currentScreen.toLowerCase()}`);
      } catch (e) {
        console.warn('Failed to initialize browser history:', e);
      }
    }

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch]);

  // Redirect to catalog if not authenticated and not on catalog/search pages
  useEffect(() => {
    const {isOtpAuthenticated, isFullAuthenticated, isOtpExtendedAuthenticated, isSilentOtpAuthenticated} = loginData || {};
    const isAuthenticated = isFullAuthenticated || isOtpAuthenticated || isOtpExtendedAuthenticated || isSilentOtpAuthenticated;

    if (!isAuthenticated && currentScreen !== ScreensEnum.CATALOG && currentScreen !== ScreensEnum.SEARCH && (currentScreen !== ScreensEnum.SONGS_PAGE && !isGalleryMode && !isEditPlaylistFlow && !isChangeDefaultSongFlow)) {
      navigateToCatalog();
    }
  }, [
    loginData?.isOtpAuthenticated,
    loginData?.isFullAuthenticated,
    loginData?.isOtpExtendedAuthenticated,
    loginData?.isSilentOtpAuthenticated,
    currentScreen,
    navigateToCatalog,
  ]);

  // Redirect to catalog if trying to access admin panel without service
  useEffect(() => {
    if (currentScreen === ScreensEnum.ADMIN_PANEL && subscribersData) {
      // Check if any subscriber has service
      const anySubscriberHasService = subscribersData.some(subscriber => subscriber.hasService);
      if (!anySubscriberHasService) {
        navigateToCatalog();
      }
    }
  }, [currentScreen, subscribersData, navigateToCatalog]);

  // Stop audio playback when navigating away from pages
  useEffect(() => {
    // Import audioPlayer dynamically to avoid issues
    import('./Utils/AudioPlayer').then(({ audioPlayer }) => {
      audioPlayer.stop();
    });
  }, [currentScreen]);

  const handleNoSubscriberAction = useCallback(() => navigateToError(ErrorsEnum.NOT_SUBSCRIBED), [navigateToError]);

  const handleSubscriberSelect = useCallback(
    (subscriberNumber) => {
      console.log(subscribersData);
      console.log(props.funtoneSlice);
      const subscriber = subscribersData.find((sub) => sub.cli === subscriberNumber);
      setSelectedSubscriber(subscriber);
      // Navigation to error screen (if needed) is handled automatically in the reducer
    },
    [setSelectedSubscriber, subscribersData],
  );

  const handleOneSubscriberAction = useCallback(
    (subscriber) => {
      const subscriberObj = subscribersData.find((sub) => sub.cli === subscriber);
      setSelectedSubscriber(subscriberObj);
      // If validation passes (no error screen), navigate to package select
      // The validation is handled in the reducer, so we check after dispatch completes
      // Note: Since setSelectedSubscriber is synchronous via Redux, we need to check validation here
      const validationError = validateSubscriberForError(subscriberObj);
      if (!validationError) {
        navigateToPackageSelect();
      }
    },
    [navigateToPackageSelect, setSelectedSubscriber, subscribersData],
  );

  const handleSubmit = useCallback(() => {
    // Validate the selected subscriber before navigating
    const selectedSubscriber = props.funtoneSlice?.selectedSubscriber;
    if (selectedSubscriber) {
      const validationError = validateSubscriberForError(selectedSubscriber);
      if (validationError) {
        // Set error and navigate to error screen
        dispatch(setCurrentScreen(ScreensEnum.ERROR));
        // Need to set the error reason - dispatch a custom action or use navigateToError
        navigateToError(validationError);
        return;
      }
    }
    navigateToPackageSelect();
  }, [navigateToPackageSelect, navigateToError, props.funtoneSlice?.selectedSubscriber, dispatch]);

  let currentScreenComponent = null;
  let customBackHandler = undefined;
  let showBackButton = true;
  
  const handleClosePopUp = useCallback(() => {
    if (closePopUp) closePopUp();
    else if (setPopUp) setPopUp(null);
  }, [closePopUp, setPopUp]);

  switch (currentScreen) {
    case ScreensEnum.CATALOG:
      currentScreenComponent = <Catalog {...props} />;
      break;
    case ScreensEnum.SEARCH:
      currentScreenComponent = <SearchPage />;
      break;
    case ScreensEnum.SONGS_PAGE:
      currentScreenComponent = <SongsPage {...props} />;
      break;
    case ScreensEnum.SUBSCRIBER_SELECT:
      currentScreenComponent = (
        <SelectSubscriber
          title={'עם איזה מנוי להמשיך?'}
          submitButtonText={'בחרתי, אפשר להמשיך'}
          onSubmit={handleSubmit}
          hideRadioButtons={false}
          onNoSubscriberAction={handleNoSubscriberAction}
          onSubscriberSelect={handleSubscriberSelect}
          onOneSubscriberAction={handleOneSubscriberAction}
        />
      );
      break;
    case ScreensEnum.PACKAGE_SELECT:
      currentScreenComponent = <PackageSelect {...props} />;
      break;
    case ScreensEnum.SUCCESS:
      currentScreenComponent = <SuccessPage {...props} />;
      break;
    case ScreensEnum.ERROR:
      const errorContent = ErrorContent({...props, dispatch});
      currentScreenComponent = <ErrorStep content={errorContent} />;
      if (errorContent?.hideBackButton) {
        showBackButton = false;
      }
      break;
    case ScreensEnum.ADMIN_PANEL:
      currentScreenComponent = <AdminPanel {...props} />;
      customBackHandler = () => {
        dispatch(setCurrentScreen(ScreensEnum.CATALOG));
      };
      break;
    case ScreensEnum.SONG_SETTINGS:
      currentScreenComponent = <SongSettings {...props} />;
      break;
    case ScreensEnum.CONFIGURE_SONGS:
      currentScreenComponent = <ConfigureSongs {...props} />;
      break;
    case ScreensEnum.TERMS_AND_CONDITIONS:
      currentScreenComponent = <TermsAndConditions {...props} />;
      break;
    default:
      currentScreenComponent = <Catalog {...props} />;
  }


  const handleConfirmDelete = useCallback(async () => {
    const songToDelete = props.funtoneSlice?.selectedSong;
    const selectedSubscriber = props.funtoneSlice?.selectedSubscriber;
    const isEditPlaylistFlow = props.funtoneSlice?.isEditPlaylistFlow;

    if (songToDelete && selectedSubscriber) {
      console.log('Deleting song:', songToDelete.id, 'for subscriber:', selectedSubscriber?.cli);

      // Report delete song initiated
      const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);

      // Use different reports based on whether we're in edit playlist mode
      if (isEditPlaylistFlow) {
        dwhFuntoneRemoveSongFromPlaylist({
          ...reportParams,
          ban: props.loginData?.userDetails?.ban || '',
          crmId: props.loginData?.userDetails?.crmId || '',
        });
      } else {
        dwhFuntoneSongDelete({
          ...reportParams,
          ban: props.loginData?.userDetails?.ban || '',
          crmId: props.loginData?.userDetails?.crmId || '',
          songName: songToDelete?.description || songToDelete?.title || '',
        });
      }

      const result = await dispatch(
        deleteSong({
          subscriber: selectedSubscriber?.cli,
          productId: songToDelete.productId || songToDelete.id,
        }),
      );

      // Report success/failure based on result
      if (result.type.endsWith('/fulfilled')) {
        if (isEditPlaylistFlow) {
          dwhFuntoneRemoveSongFromPlaylistSuccess({
            ...reportParams,
            ban: props.loginData?.userDetails?.ban || '',
            crmId: props.loginData?.userDetails?.crmId || '',
          });
        } else {
          dwhFuntoneSongDeleteSuccess({
            ...reportParams,
            ban: props.loginData?.userDetails?.ban || '',
            crmId: props.loginData?.userDetails?.crmId || '',
            songName: songToDelete?.description || songToDelete?.title || '',
          });
        }
      } else if (result.type.endsWith('/rejected')) {
        if (isEditPlaylistFlow) {
          dwhFuntoneRemoveSongFromPlaylistFailed({
            ...reportParams,
            ban: props.loginData?.userDetails?.ban || '',
            crmId: props.loginData?.userDetails?.crmId || '',
          });
        } else {
          dwhFuntoneSongDeleteFailed({
            ...reportParams,
            ban: props.loginData?.userDetails?.ban || '',
            crmId: props.loginData?.userDetails?.crmId || '',
            songName: songToDelete?.description || songToDelete?.title || '',
          });
        }
      }
    }
    handleClosePopUp();
  }, [
    props.funtoneSlice?.selectedSong,
    props.funtoneSlice?.selectedSubscriber,
    props.funtoneSlice?.isEditPlaylistFlow,
    props.loginData,
    dispatch,
    handleClosePopUp,
  ]);

  const handleConfirmRemoveContact = useCallback(async () => {
    const contactToRemove = props.funtoneSlice?.popUp?.data;
    const selectedSubscriber = props.funtoneSlice?.selectedSubscriber;
    const selectedSong = props.funtoneSlice?.selectedSong;

    if (contactToRemove && selectedSubscriber && selectedSong) {
      console.log('Removing contact:', contactToRemove.profileId, 'for subscriber:', selectedSubscriber?.cli);

      // Report remove song from contact initiated
      const reportParams = getCommonReportParams(selectedSubscriber, props.loginData);
      dwhFuntoneRemoveSongFromContact({
        ...reportParams,
        ban: props.loginData?.userDetails?.ban || '',
        crmId: props.loginData?.userDetails?.crmId || '',
      });

      const deleteAction = await dispatch(
        deleteContact({
          subscriber: selectedSubscriber?.cli,
          profileId: contactToRemove,
        }),
      );

      if (deleteAction.type.endsWith('/fulfilled')) {
        // Report remove song from contact success
        dwhFuntoneRemoveSongFromContactSuccess({
          ...reportParams,
          ban: props.loginData?.userDetails?.ban || '',
          crmId: props.loginData?.userDetails?.crmId || '',
        });

        // Refresh the gallery to get updated contact list
        const galleryAction = await dispatch(getGallery({subscriber: selectedSubscriber?.cli}));

        if (galleryAction.type.endsWith('/fulfilled')) {
          // Update selectedSong with the refreshed data from gallery
          const subscriberGallery = galleryAction.payload?.results?.subscriberGallery;
          const freshGallery = subscriberGallery?.items?.item || [];
          const galleryArray = Array.isArray(freshGallery) ? freshGallery : [freshGallery];
          const songId = selectedSong?.productId || selectedSong?.id;

          const updatedSong = galleryArray.find((s) => {
            const itemId = s.productId || s.id || s.funId;
            return itemId == songId; // Use loose equality to handle string vs number
          });

          if (updatedSong) {
            dispatch(setSelectedSong(updatedSong));
          }
        }
      } else {
        // Report remove song from contact failure
        dwhFuntoneRemoveSongFromContactFailed({
          ...reportParams,
          ban: props.loginData?.userDetails?.ban || '',
          crmId: props.loginData?.userDetails?.crmId || '',
        });
      }
    }
    handleClosePopUp();
  }, [
    props.funtoneSlice?.popUp?.data,
    props.funtoneSlice?.selectedSubscriber,
    props.funtoneSlice?.selectedSong,
    props.loginData,
    dispatch,
    handleClosePopUp,
  ]);

  const handleConfirmRemoveFromPlaylist = useCallback(async () => {
    const songToRemove = props.funtoneSlice?.popUp?.data;
    const selectedSubscriber = props.funtoneSlice?.selectedSubscriber;

    if (songToRemove && selectedSubscriber) {
      console.log('Removing song from playlist:', songToRemove.productId || songToRemove.id);

      await dispatch(
        deleteSong({
          subscriber: selectedSubscriber?.cli,
          productId: songToRemove.productId || songToRemove.id,
        }),
      );

      // Clear selectedSong if it was the removed song
      const selectedSong = props.funtoneSlice?.selectedSong;
      if (selectedSong && (selectedSong.productId === songToRemove.productId || selectedSong.id === songToRemove.id)) {
        dispatch(setSelectedSong(null));
      }
    }
    handleClosePopUp();
  }, [
    props.funtoneSlice?.popUp?.data,
    props.funtoneSlice?.selectedSubscriber,
    props.funtoneSlice?.selectedSong,
    dispatch,
    handleClosePopUp,
  ]);

  return (
    <div className='funtone-main-container'>
      <FuntoneScreen {...props} onBackClick={customBackHandler} showBackButton={showBackButton}>{currentScreenComponent}</FuntoneScreen>
      <PopUp
        showPopUp={props.funtoneSlice?.popUp?.type}
        onClose={handleClosePopUp}
        hideCloseButton={
          props.funtoneSlice?.popUp?.type === PopUpEnum.DELETE_SONG || props.funtoneSlice?.popUp?.type === PopUpEnum.REMOVE_CONTACT
        }
        className={
          props.funtoneSlice?.popUp?.type === PopUpEnum.LOGIN
            ? 'login-popup-wrapper'
            : props.funtoneSlice?.popUp?.type
            ? 'confirmation-popup-wrapper'
            : ''
        }>
        {props.funtoneSlice?.popUp?.type === PopUpEnum.LOGIN ? <LoginPopUp /> : null}
        {props.funtoneSlice?.popUp?.type === PopUpEnum.DELETE_SONG ? (
          <ConfirmationPopUp
            title={'למחוק את השיר מהפלייליסט שלך?'}
            confirmText='מחיקה'
            cancelText='ביטול'
            onConfirmDelete={handleConfirmDelete}
            onCancel={handleClosePopUp}
          />
        ) : null}
        {props.funtoneSlice?.popUp?.type === PopUpEnum.REMOVE_CONTACT ? (
          <ConfirmationPopUp
            title={'להסיר את איש הקשר מהשיר?'}
            confirmText='כן, הסירו'
            cancelText='ביטול'
            onConfirmDelete={handleConfirmRemoveContact}
            onCancel={handleClosePopUp}
          />
        ) : null}
      </PopUp>
      <Toast message={toast?.message || ''} isVisible={toast?.isVisible || false} onClose={() => dispatch(hideToast())} />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    funtoneSlice: state.FuntoneSlice,
    loginData: state.authentication.loginData,
  };
};

const mapDispatchToProps = {
  doSearch,
  getCatalog,
  setPopUp,
  closePopUp,
  setSelectedSubscriber,
  getSubscribersData,
  resetState
};

export default connect(mapStateToProps, mapDispatchToProps)(Funtone);
